import { api } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import type { GuildDetail, GuildMemberRow, MeUser } from '@/types/api';

/**
 * The cached guild roster (detail + every member) and its live upkeep.
 *
 * A member joining, leaving, getting a role or changing their avatar used to
 * make EVERY online client of the guild refetch the whole detail — channels,
 * roles and members — a hundred heavy requests for one avatar. The socket
 * events already carry the changed row, so they patch the cache in place;
 * only role edits (roles live in the detail) reload it.
 */

export function guildRosterKey(guildId: string) {
  return ['guild', guildId] as const;
}

/** Most roster pages one load follows (1000 members each). */
const MAX_MEMBER_PAGES = 50;

/** A guild's detail with its whole roster: the first page rides the detail,
 *  the rest follow its cursor. */
export async function loadGuildDetail(guildId: string): Promise<GuildDetail> {
  const detail = await api.getGuild(guildId);
  let members = detail.members;
  let cursor = detail.membersNextCursor ?? null;
  for (let page = 0; cursor && page < MAX_MEMBER_PAGES; page += 1) {
    const next = await api.listGuildMembers(guildId, cursor);
    members = members.concat(next.members);
    cursor = next.nextCursor;
  }
  return { ...detail, members, membersNextCursor: cursor };
}

function patch(guildId: string, fn: (detail: GuildDetail) => GuildDetail): void {
  queryClient.setQueryData<GuildDetail>(guildRosterKey(guildId), (detail) => (detail ? fn(detail) : detail));
}

/** Role counts after some roles lost a holder and others gained one. */
function recount(
  counts: Record<string, number> | undefined,
  lost: readonly string[],
  gained: readonly string[],
): Record<string, number> | undefined {
  if (!counts || (lost.length === 0 && gained.length === 0)) return counts;
  const next = { ...counts };
  for (const roleId of lost) next[roleId] = Math.max(0, (next[roleId] ?? 0) - 1);
  for (const roleId of gained) next[roleId] = (next[roleId] ?? 0) + 1;
  return next;
}

export const rosterCache = {
  memberJoined(member: GuildMemberRow): void {
    patch(member.guildId, (detail) =>
      detail.members.some((m) => m.userId === member.userId)
        ? detail
        : {
            ...detail,
            members: [...detail.members, member],
            memberCount: (detail.memberCount ?? detail.members.length) + 1,
            roleMemberCounts: recount(detail.roleMemberCounts, [], member.roleIds),
          },
    );
  },

  /** Roles, nickname or timeout changed. The event's user may carry fewer
   *  fields than the roster row, so the user is merged, not replaced. */
  memberUpdated(member: GuildMemberRow): void {
    patch(member.guildId, (detail) => {
      const old = detail.members.find((m) => m.userId === member.userId);
      if (!old) return detail;
      return {
        ...detail,
        members: detail.members.map((m) =>
          m.userId === member.userId ? { ...m, ...member, user: { ...m.user, ...member.user } } : m,
        ),
        roleMemberCounts: recount(
          detail.roleMemberCounts,
          old.roleIds.filter((id) => !member.roleIds.includes(id)),
          member.roleIds.filter((id) => !old.roleIds.includes(id)),
        ),
      };
    });
  },

  memberLeft(guildId: string, userId: string): void {
    patch(guildId, (detail) => {
      const old = detail.members.find((m) => m.userId === userId);
      if (!old) return detail;
      return {
        ...detail,
        members: detail.members.filter((m) => m.userId !== userId),
        memberCount: Math.max(0, (detail.memberCount ?? detail.members.length) - 1),
        roleMemberCounts: recount(detail.roleMemberCounts, old.roleIds, []),
      };
    });
  },

  /** A member's profile changed (name, avatar, status, team). */
  userUpdated(guildId: string, user: Partial<MeUser> & { id: string }): void {
    patch(guildId, (detail) =>
      detail.members.some((m) => m.userId === user.id)
        ? {
            ...detail,
            members: detail.members.map((m) => (m.userId === user.id ? { ...m, user: { ...m.user, ...user } } : m)),
          }
        : detail,
    );
  },

  /** Roles changed (they live in the detail): load it again. */
  reload(guildId: string): void {
    void queryClient.invalidateQueries({ queryKey: guildRosterKey(guildId), exact: true });
  },
};
